﻿import { createFileRoute } from "@tanstack/react-router";
import { TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useCompany } from "@/lib/CompanyProvider";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useGrowthData } from "@/hooks/growth/useGrowthData";

import { FinancialHistoryPanel } from "@/components/growth/FinancialHistoryPanel";
import { RevenuePanel } from "@/components/growth/RevenuePanel";
import { ChurnPanel } from "@/components/growth/ChurnPanel";
import { CouponsPanel } from "@/components/growth/CouponsPanel";

export const Route = createFileRoute("/app/growth")({
  component: GrowthModule,
});

function GrowthModule() {
  useDocumentMeta({
    title: "AI Growth & Relatórios - AutoWash Pro",
    description:
      "Acompanhe histórico financeiro, métricas, cupons e inteligência de crescimento.",
  });

  const { activeCompanyId } = useCompany();
  const growthData = useGrowthData(activeCompanyId);

  if (growthData.loadingOrders || growthData.loadingChurn) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg">
            <Skeleton className="h-6 w-48 mb-6" />
            <div className="flex justify-between items-end mb-4">
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-10 w-32" />
            </div>
            <Skeleton className="h-2 w-full rounded-full mt-4" />
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg">
            <Skeleton className="h-6 w-48 mb-6" />
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={`gskel-${i}`}
                  className="flex justify-between items-center"
                >
                  <div>
                    <Skeleton className="h-4 w-32 mb-1" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-8 w-16 rounded-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <TrendingUp className="text-blue-500" size={32} />
            AI Growth e Histórico Financeiro
          </h1>
          <p className="text-slate-400 mt-1">
            Algoritmos e estatísticas para prever faturamento, histórico
            financeiro e recuperar clientes inativos.
          </p>
        </div>
      </div>

      <FinancialHistoryPanel activeCompanyId={activeCompanyId} />

      <RevenuePanel data={growthData} />

      <ChurnPanel data={growthData} />

      <CouponsPanel activeCompanyId={activeCompanyId} data={growthData} />
    </div>
  );
}
